/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { css, StyleSheet } from 'aphrodite';
import React, { ReactElement, useState } from 'react';
import { useLocation } from 'react-router';

import { useRootSelector } from '../../../hooks/useRootSelector';
import { MultiPdfViewer } from '../PdfViewer/MultiPdfViewer';
import { SigningModal } from './SigningModal';
import { WaitAfterSigningModal } from './WaitAfterSigningModal';
import { useDocumentViewing } from './hooks/useDocumentViewing';
import { useDocumentSigning } from './hooks/useDocumentSigning';
import { useOneSpanSigning } from './hooks/useOneSpanSigning';
import { DocumentSubheader } from './DocumentSubheader';
import { NotaryDocumentHeader } from './NotaryDocumentHeader';
import { DocumentSigningIframe } from './DocumentSigningIframe';
import { DocumentViewer } from './DocumentViewer/DocumentViewer';
import { DocumentPackageReviewBar } from './DocumentPackageReviewBar';
import { useReviewDocumentPdfs } from './hooks/useReviewDocumentPdfs';
import { isNotaryRoute } from '@utils/routing';
import { noop } from '@utils/lodash';
import { SigningActionType } from '@shared/constants/signing';
import { DocumentStatus } from '@shared/constants/document';

export namespace Signing {
  export interface Props {
    setSelectedDocumentAndFetchPdf: (documentId: string) => void;
  }
}

export const styles = StyleSheet.create({
  wrapper: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
});

export const Signing: React.FC<Signing.Props> = ({ setSelectedDocumentAndFetchPdf }) => {
  const location = useLocation();
  const isNotary = isNotaryRoute(location.pathname);
  const { signingRoom, callDetails, document, layout } = useRootSelector(state => state);
  const { signingAction } = signingRoom;
  const { selectedDoc, editUrl, documents } = document;
  const { isMobileDevice } = layout;
  const [isWaitAfterSigningModalDismissed, setIsWaitAfterSigningModalDismissed] = useState(false);
  const [signingUrl, setSigningUrl] = useState('');
  const reviewDocs = useReviewDocumentPdfs();

  const { selectedDocument } = useDocumentViewing(setSelectedDocumentAndFetchPdf);
  const { isSigningModalOpen, setIsSigningModalOpen } = useDocumentSigning(
    setSelectedDocumentAndFetchPdf,
    setSigningUrl,
  );
  useOneSpanSigning(setSigningUrl);

  const shouldShowEditIframe =
    isNotary && signingAction.type === SigningActionType.WAIT_FOR_SIGNING_TO_START && !!editUrl;
  const shouldShowSigningIframe = signingAction.type === SigningActionType.SIGN && !isSigningModalOpen;
  const shouldShowWaitAfterSigningModal =
    isNotary &&
    signingAction.type === SigningActionType.WAIT_FOR_POST_SIGN_PROCESSING &&
    !isWaitAfterSigningModalDismissed;
  const activeDocument = documents.find(doc => doc.id === selectedDoc?.id);
  const isActiveDocumentUnderReview = activeDocument?.status === DocumentStatus.PENDING_REVIEW;
  const isNotaryReviewing = isNotary && signingAction.type === SigningActionType.REVIEW;
  const shouldShowReviewBar = isActiveDocumentUnderReview && isNotaryReviewing;

  const documentsPendingReview = document?.documents.filter(doc => doc.status === DocumentStatus.PENDING_REVIEW);
  const documentsCompleted = document?.documents.filter(doc => doc.status === DocumentStatus.COMPLETED);
  const isLastUnsignedDocumentPackage = documents.length - documentsCompleted.length === documentsPendingReview.length;

  const getDocumentViewer = (): ReactElement | undefined => {
    if (shouldShowEditIframe || shouldShowSigningIframe) {
      return (
        <DocumentSigningIframe
          selectedDocument={selectedDocument}
          shouldShowEditIframe={shouldShowEditIframe}
          editUrl={editUrl}
          signingUrl={signingUrl}
        />
      );
    } else if (!isNotaryReviewing) {
      return (
        <DocumentViewer
          selectedPdfUrl={selectedDoc?.pdfUrl}
          selectedDocStatus={selectedDocument?.status}
          shouldShowSigningIframe={shouldShowSigningIframe}
        />
      );
    } else if (reviewDocs && reviewDocs.length > 0) {
      return <MultiPdfViewer documents={reviewDocs} />;
    }

    return undefined;
  };

  return (
    <div className={css(styles.wrapper)} data-it="Signing">
      <SigningModal
        contentLabel="signing_modal"
        documentName={signingAction.document ? signingAction.document.name : ''}
        signerName={signingAction.signerName ? signingAction.signerName : ''}
        isOpen={isSigningModalOpen}
        onSubmit={() => {
          setIsSigningModalOpen(false);
        }}
        handleClose={noop}
      />
      <WaitAfterSigningModal
        isOpen={shouldShowWaitAfterSigningModal}
        setIsDismissed={setIsWaitAfterSigningModalDismissed}
      />
      {isNotary && <NotaryDocumentHeader />}
      {!isMobileDevice && !isNotaryReviewing && (
        <DocumentSubheader
          isNotary={isNotary}
          selectedDocument={selectedDocument}
          shouldShowSigningIframe={shouldShowSigningIframe}
        />
      )}
      {getDocumentViewer()}
      {shouldShowReviewBar && reviewDocs && (
        <DocumentPackageReviewBar
          callId={callDetails!.id}
          documentIds={reviewDocs.map(d => d.id)}
          isLastUnsignedDocumentPackage={isLastUnsignedDocumentPackage}
        />
      )}
    </div>
  );
};
